import * as React from 'react';

import { Container } from '@/components/ui/container';
import type { LegalBlock, LegalDoc } from '@/content/legal';

type LegalTab = {
  id: string;
  label: string;
  doc: LegalDoc;
};

/** Renders bold Markdown and absolute URLs inside legal-document text. */
function renderInline(text: string) {
  return text.split(/(\*\*[^*]+\*\*|https?:\/\/[^\s)]+)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-ink-900">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith('http://') || part.startsWith('https://')) {
      return (
        <a
          key={i}
          className="font-medium text-ink-900 underline decoration-ink-300 underline-offset-4 transition-colors hover:decoration-ink-900"
          href={part}
        >
          {part}
        </a>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

/** Renders one structured block within a legal document. */
function Block({ block }: { block: LegalBlock }) {
  switch (block.type) {
    case 'h2':
      return (
        <h2 className="mt-12 text-xl font-semibold tracking-tight text-ink-900">
          {renderInline(block.text)}
        </h2>
      );
    case 'h3':
      return (
        <h3 className="mt-6 text-base font-semibold text-ink-900">
          {renderInline(block.text)}
        </h3>
      );
    case 'ul':
      return (
        <ul className="ml-5 list-disc space-y-2 marker:text-ink-300">
          {block.items.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr>
                {block.headers.map((h) => (
                  <th
                    key={h}
                    className="border border-hairline bg-ink-50 px-4 py-2.5 font-semibold text-ink-900"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r} className="align-top">
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      className="border border-hairline px-4 py-3 text-ink-600"
                    >
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'p':
    default:
      return <p>{renderInline(block.text)}</p>;
  }
}

/** Renders a legal document's date, introduction, and sections. */
function LegalDocument({ doc }: { doc: LegalDoc }) {
  return (
    <>
      {doc.lastUpdated && (
        <p className="mt-3 text-sm text-ink-500">
          Last updated: {doc.lastUpdated}
        </p>
      )}

      {doc.intro.length > 0 && (
        <div className="mt-6 space-y-4 text-sm leading-relaxed text-ink-600">
          {doc.intro.map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </div>
      )}

      <div className="mt-12 space-y-10">
        {doc.sections.map((sec) => (
          <section key={sec.heading}>
            <h2 className="text-xl font-semibold tracking-tight text-ink-900">
              {sec.heading}
            </h2>
            <div className="mt-4 space-y-4 text-sm leading-relaxed text-ink-600">
              {sec.blocks.map((block, i) => (
                <Block key={i} block={block} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

/** A long-form legal page (Terms, Privacy), driven by structured content. */
export function LegalPage({ doc, tabs }: { doc: LegalDoc; tabs?: LegalTab[] }) {
  const [activeTabId, setActiveTabId] = React.useState(tabs?.[0]?.id);
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const activeTab = tabs?.find((tab) => tab.id === activeTabId) ?? tabs?.[0];
  const pageTitle = doc.title;

  function handleTabKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (
      !tabs ||
      !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
    ) {
      return;
    }

    event.preventDefault();
    const currentIndex = tabs.findIndex((tab) => tab.id === activeTabId);
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? tabs.length - 1
          : (currentIndex +
              (event.key === 'ArrowRight' ? 1 : -1) +
              tabs.length) %
            tabs.length;
    const nextTab = tabs[nextIndex];

    if (!nextTab) {
      return;
    }

    setActiveTabId(nextTab.id);
    tabRefs.current[nextIndex]?.focus();
  }

  return (
    <section className="bg-white pb-20 pt-28 sm:pb-24 sm:pt-32">
      <Container className="max-w-3xl">
        <h1 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
          {pageTitle}
        </h1>

        {tabs && activeTab && (
          <div className="mt-8 border-b border-hairline">
            <div
              aria-label="Terms categories"
              className="-mb-px flex gap-6"
              role="tablist"
            >
              {tabs.map((tab, index) => {
                const isActive = tab.id === activeTab.id;
                return (
                  <button
                    key={tab.id}
                    ref={(element) => {
                      tabRefs.current[index] = element;
                    }}
                    aria-controls={`terms-panel-${tab.id}`}
                    aria-selected={isActive}
                    className={`border-b-2 px-0.5 pb-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-900 focus-visible:ring-offset-4 ${
                      isActive
                        ? 'border-ink-900 text-ink-900'
                        : 'border-transparent text-ink-500 hover:border-ink-300 hover:text-ink-700'
                    }`}
                    id={`terms-tab-${tab.id}`}
                    onClick={() => setActiveTabId(tab.id)}
                    onKeyDown={handleTabKeyDown}
                    role="tab"
                    tabIndex={isActive ? 0 : -1}
                    type="button"
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {tabs ? (
          tabs.map((tab) => {
            const isActive = tab.id === activeTab?.id;
            return (
              <div
                key={tab.id}
                aria-labelledby={`terms-tab-${tab.id}`}
                hidden={!isActive}
                id={`terms-panel-${tab.id}`}
                role="tabpanel"
              >
                <LegalDocument doc={tab.doc} />
              </div>
            );
          })
        ) : (
          <LegalDocument doc={doc} />
        )}
      </Container>
    </section>
  );
}
